
import React, { useEffect, useRef, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { DataUrlStore, loadData } from "../loadData";
import styled from "styled-components";
import { Embed } from "../utils/html-embeds";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { withLocale } from "../utils/locale";
import { Trans, useTranslation } from "react-i18next";
import { Header, PoweredByScoopIt } from "./common-header";
import { EmbedContainer } from "./common-container";
import { Description, Insight, Meta, Title } from "./common-posts";
import { Paginator } from "./common-pagination";
import { Tags } from "./common-tags";

dayjs.extend(LocalizedFormat)



// basic responsive grid.
//
// extra params:
// - maxPosts
// - colSplitWidth 
// - minCols
// - maxCols
// - locale
// - sections (default: ["title", "media", "meta", "content", "insight"])
// - displayHeader (default: true)
// - disableMediaEmbeds
// - displayPaginator (default: true)
// displayTags: (default: false)

export const DEFAULT_SECTIONS = ["title", "media", "meta", "content", "insight"];

const ResponsiveRenderer = ({
    data,
    disableMediaEmbeds,
    maxPosts,
    colSplitWidth,
    minCols = 1,
    maxCols = 8,
    locale,
    displayHeader = true,
    sections = DEFAULT_SECTIONS,
    displayPoweredByScoopIt = true,
    displayPaginator = true,
    displayTags = false,
    tagCloudTitle = null,
    maxTagsCount = null,
}) => {
    dayjs.locale(locale);
    const { t } = useTranslation();
    const ref = useRef();
    const [cols, setCols] = useState(0);
    useEffect(() => {
        const computeCols = () => {
            const elt = ref.current;
            const width = elt.offsetWidth;
            const nCols = Math.min(Math.max(Math.round(width / (colSplitWidth || 400)), minCols), maxCols);
            if (nCols != cols) {
                setCols(nCols);
            }
        }
        window.addEventListener("resize", computeCols);
        computeCols();
        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", computeCols);
    });
    if (cols === 0) {
        // render nothing at first, si we can compute the number of columns
        return <div ref={ref}>&nbsp;</div>
    }
    const headerImageSizeClasses = cols === 1 ? "scp-w-20 scp-h-20" : "scp-w-28 scp-h-28";

    return <EmbedContainer ref={ref}>
        <Header
            data={data}
            displayHeader={displayHeader}
            outsidePoweredBy={cols == 1}
            headerImageSizeClasses={headerImageSizeClasses}
            displayPoweredByScoopIt={displayPoweredByScoopIt}
        />
        {displayTags && <Tags tags={data.tags} maxTagsCount={maxTagsCount} tagCloudTitle={tagCloudTitle} />}

        <div className="scp-flex scp-gap-4 scp-embed-columns-container">
            {
                Array(cols).fill(0).map((_, col) => <div className="scp-flex-1 scp-flex scp-flex-col scp-gap-4 scp-embed-column" key={col}>
                    {
                        data.posts.map((post, i) => (!maxPosts || maxPosts > i) && (i % cols === col) && <ResponsivePost sections={sections} post={post} disableEmbeds={disableMediaEmbeds} key={i} />)
                    }
                </div>)
            }
        </div>
        {displayPaginator && <Paginator />}
        {
            displayPoweredByScoopIt && data.creator && <PoweredByScoopIt />
        }
    </EmbedContainer>
}

const ResponsivePost = ({ sections, post, disableEmbeds }) => {
    const displayEmbed = post.html_embed && !disableEmbeds;
    return <div className="scp-p-4 scp-rounded-sm scp-shadow-md scp-embed-block scp-embed-post-container scp-flex scp-flex-col scp-gap-2">
        {sections.map((section, i) => {
            switch (section) {
                case "title":
                    return <Title href={post.url} key={i}>{post.title}</Title>;
                case "media":
                    if (displayEmbed) {
                        return <Embed html={post.html_embed} meta={post.embed_meta} key={i} />;
                    } else if (!displayEmbed && post.image_url) {
                        return <a href={post.url} target="_blank" className="scp-embed-post-image" key={i}><img src={post.image_url} className="scp-w-full scp-h-auto" /></a>;
                    } else {
                        return null;
                    }
                case "meta":
                    return <Meta {...post} key={i} />;
                case "content":
                    return <Description dangerouslySetInnerHTML={{ __html: post.html_content }} key={i} />;
                case "insight":
                    return <Insight {...post} key={i} />;
            }

        })
        }

    </div >
}

export const Responsive = loadData(withLocale(ResponsiveRenderer));